import { AppUser } from "features/user/types";
import { BaseColorKey } from "features/user/constants";

/**
 * ウィザードのステップ
 */
export enum PlayerWizardStep {
  NAME_INPUT = "name",
  LANGUAGE = "language",
  ICON_METHOD = "icon_method",
  PRESET_ICON = "preset_icon",
  UPLOAD_ICON = "upload_icon",
  PROFILE_AI = "profile_ai",
  PREVIEW = "preview",
}

/**
 * アイコン選択方法
 */
export enum PlayerIconMethod {
  PRESET = "preset",
  UPLOAD = "upload",
  PROFILE_AI = "profile_ai",
}

/**
 * プロフィール入力のサブステップ
 */
export enum PlayerProfileSubStep {
  GENDER = "gender",
  BIRTHDAY = "birthday",
  FAVORITES = "favorites",
  ICON_PREVIEW = "icon_preview",
}

/**
 * 好きなもの選択のサブステップ
 */
export enum PlayerFavoritesSubStep {
  APPEARANCE = "appearance",
  COLOR = "color",
  BEST_SUBJECT = "bestSubject",
}

/**
 * 性別（3択）
 */
export enum PlayerGender {
  MALE = "male",
  FEMALE = "female",
  NONE = "none",
}

/**
 * 容姿の型定義
 */
export type AccessoryType = "none" | "megane" | "boushi" | "hairpin" | "ribbon" | "bandaid";
export type HairstyleType = "very_short" | "short" | "medium" | "slightly_long" | "long";
export type BodyTypeType = "thin" | "slightly_thin" | "medium" | "slightly_chubby" | "chubby";
export type FaceShapeType = "round" | "oval" | "oblong" | "square" | "triangle";

/**
 * アップロードアイコンのメタデータ
 */
export interface UploadedIconMetadata {
  source: "uploaded";
  filename: string;
  content_type: string;
  file_size: number;
  width: string;
  height: string;
}

/**
 * ウィザードの状態
 */
export interface CreatePlayerState {
  // 基本情報
  name: string;
  locale: string;
  icon: string;
  iconMethod: PlayerIconMethod | null;

  // プロフィール情報
  gender: PlayerGender | null;
  birthday: string;
  birthdayYear: number | null;
  birthdayMonth: number | null;
  birthdayDay: number | null;

  // 好きないろ（2段階選択）
  selectedBaseColors: BaseColorKey[];
  favoriteColors: string[];

  // 得意な教科（複数選択）
  bestSubjects: string[];

  // 容姿
  accessory: AccessoryType[];
  hairstyle: HairstyleType | null;
  bodyType: BodyTypeType | null;
  faceShape: FaceShapeType | null;

  // アップロードアイコンのメタデータ
  iconMetadata?: UploadedIconMetadata;
}

/**
 * ステップ定義
 */
export interface PlayerStepDefinition {
  key: PlayerWizardStep;
  labelKey: string;
  icon: string;
}

/**
 * 全サブステップ定義（フラット・6ステップ）
 */
export interface AllPlayerProfileStepDefinition {
  id: string;
  profileSubStep: PlayerProfileSubStep;
  favoritesSubStep?: PlayerFavoritesSubStep;
  labelKey: string;
  number: number;
}

/**
 * 全サブステップ一覧（6ステップ）
 * 順序: 性別 → 誕生日 → みため → 好きないろ → 得意な教科 → アイコン
 */
export const ALL_PLAYER_PROFILE_STEPS: AllPlayerProfileStepDefinition[] = [
  {
    id: "gender",
    profileSubStep: PlayerProfileSubStep.GENDER,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_GENDER",
    number: 1,
  },
  {
    id: "birthday",
    profileSubStep: PlayerProfileSubStep.BIRTHDAY,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_BIRTHDAY",
    number: 2,
  },
  {
    id: "appearance",
    profileSubStep: PlayerProfileSubStep.FAVORITES,
    favoritesSubStep: PlayerFavoritesSubStep.APPEARANCE,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_APPEARANCE",
    number: 3,
  },
  {
    id: "color",
    profileSubStep: PlayerProfileSubStep.FAVORITES,
    favoritesSubStep: PlayerFavoritesSubStep.COLOR,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_COLOR",
    number: 4,
  },
  {
    id: "bestSubject",
    profileSubStep: PlayerProfileSubStep.FAVORITES,
    favoritesSubStep: PlayerFavoritesSubStep.BEST_SUBJECT,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_BEST_SUBJECT",
    number: 5,
  },
  {
    id: "icon",
    profileSubStep: PlayerProfileSubStep.ICON_PREVIEW,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_SUBSTEP_ICON",
    number: 6,
  },
];

/**
 * CreatePlayerModal のプロパティ
 */
export interface CreatePlayerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (user: AppUser) => void;
}

/**
 * ステップ定義（4ステップ）
 */
export const PLAYER_WIZARD_STEPS: PlayerStepDefinition[] = [
  {
    key: PlayerWizardStep.NAME_INPUT,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_STEP_NAME",
    icon: "edit",
  },
  {
    key: PlayerWizardStep.LANGUAGE,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_STEP_LANGUAGE",
    icon: "language",
  },
  {
    key: PlayerWizardStep.ICON_METHOD,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_STEP_ICON",
    icon: "palette",
  },
  {
    key: PlayerWizardStep.PREVIEW,
    labelKey: "MSG_PARENT_PAGE_CREATE_PLAYER_STEP_CONFIRM",
    icon: "check_circle",
  },
];

/**
 * 初期状態
 */
export const initialPlayerState: CreatePlayerState = {
  name: "",
  locale: "ja",
  icon: "",
  iconMethod: null,
  gender: null,
  birthday: "",
  birthdayYear: null,
  birthdayMonth: null,
  birthdayDay: null,
  selectedBaseColors: [],
  favoriteColors: [],
  bestSubjects: [],
  accessory: [],
  hairstyle: null,
  bodyType: null,
  faceShape: null,
  iconMetadata: undefined,
};
